import React from "react";
import { MapPin, X, Sparkle } from "@phosphor-icons/react";

/**
 * LocationCandidatesModal — muestra 2-3 candidatas devueltas por la IA
 * y permite al usuario confirmar la correcta o descartar y elegir manualmente.
 *
 * Props:
 *   open: bool
 *   candidates: [{ value, kind, label, asset_id?, confidence, uncatalogued? }]
 *   transcript: string
 *   onPick(cand): void
 *   onClose(): void
 */
export default function LocationCandidatesModal({ open, candidates = [], transcript = "", onPick, onClose }) {
  if (!open) return null;
  const list = candidates.slice(0, 3);
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end md:items-center justify-center p-0 md:p-4" onClick={onClose} data-testid="voice-candidates-modal">
      <div className="q-card w-full max-w-lg p-5 rounded-t-2xl md:rounded-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3 mb-3">
          <div className="h-9 w-9 rounded-lg flex items-center justify-center bg-[color:var(--q-navy)] text-[color:var(--q-gold)]">
            <Sparkle size={18} weight="fill"/>
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-base">Confirma la ubicación</h3>
            <p className="text-xs text-[color:var(--q-muted)] mt-0.5">
              La IA no está segura al 100%. Toca la ubicación correcta.
            </p>
          </div>
          <button onClick={onClose} data-testid="voice-candidates-close" className="text-[color:var(--q-muted)] hover:text-[color:var(--q-navy)]">
            <X size={18}/>
          </button>
        </div>

        {transcript && (
          <div className="rounded-lg bg-neutral-50 border border-[color:var(--q-border)] p-3 mb-3">
            <div className="q-label text-[color:var(--q-muted)] mb-1">Escuché</div>
            <div className="text-sm italic">"{transcript}"</div>
          </div>
        )}

        <div className="grid gap-2">
          {list.length === 0 && (
            <div className="text-sm text-[color:var(--q-muted)] p-3 text-center">
              No se pudo identificar una ubicación en tu nota. Selecciónala manualmente.
            </div>
          )}
          {list.map((c, i) => (
            <button key={i} type="button" data-testid={`voice-candidate-${i}`}
                    onClick={() => onPick(c)}
                    className="w-full text-left p-3 rounded-lg border border-[color:var(--q-border)] hover:border-[color:var(--q-navy)] hover:bg-[color:var(--q-navy)]/5 transition flex items-center gap-3">
              <MapPin size={20} weight="fill" className="text-[color:var(--q-navy)] shrink-0"/>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">{c.label || c.value}</div>
                <div className="text-[11px] text-[color:var(--q-muted)] flex items-center gap-2">
                  <span className="q-label">{c.kind}</span>
                  {c.uncatalogued && <span className="chip chip-media text-[10px]">SIN CATALOGAR</span>}
                </div>
              </div>
              <div className="text-[10px] font-mono text-[color:var(--q-muted)]">
                {Math.round((c.confidence ?? 0.5) * 100)}%
              </div>
            </button>
          ))}
        </div>

        <button onClick={onClose} data-testid="voice-candidates-manual" className="q-btn-ghost w-full mt-4">
          Ninguna · elegir manualmente
        </button>
      </div>
    </div>
  );
}
